import { Button, Dialog, DialogContent, DialogTitle, Grid, makeStyles } from '@material-ui/core';
import { defer } from '@thalesrc/js-utils';
import React, { useCallback, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import { EmergencyContactsModel, saveEmergencyContacts, updateEmergencyContacts } from 'api/hs/emergency-contacts';
import TextField from 'Common/Form/TextField';
import { AlertContext } from 'context/alert.context';
import { PHONE_MASK } from 'model';
import { useAsyncEffect } from 'utils';

const useStyles = makeStyles(theme => ({
  dialog: {
    width: '100%',
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  sideGrid: {
    display: 'grid',
    gridTemplateColumns: '100%',
    gridTemplateRows: '1fr auto',
  },
  dialogPaper: {
    width: '50vw',
  },
  dialogScroll: {
    alignItems: 'flex-start',
  },
}));

interface Props {
  open: boolean;

  onClose(success: boolean): void;

  data: EmergencyContactsModel;
}

export default function EmergencyContactForm({ open, onClose, data }: Props) {
  const classes = useStyles();
  const { setValue, getValues, reset, trigger, ...methods } = useForm();
  const { showMessage } = useContext(AlertContext);
  const { employeeId } = useParams<{ employeeId: string }>();

  const handleSubmit = useCallback(
    async values => {
      try {
        if (!data?.id) {
          await saveEmergencyContacts({ ...values, employeeId });
          showMessage('Ekleme işlemi başarılı.', 'success');
        } else {
          await updateEmergencyContacts({
            employeeId,
            id: data.id,
            ...values,
          });
          showMessage('Güncelleme işlemi başarılı.', 'success');
        }
        onClose(true);
      } catch (err) {
        showMessage('İşlem sırasında sorun oluştu.', 'error');
        onClose(false);
      }
    },
    [data, onClose, showMessage, employeeId]
  );

  useAsyncEffect(async () => {
    if (!open) {
      return;
    }
    await defer();

    if (data) {
      setValue('name', data.name);
      setValue('phoneNumber', data.phoneNumber);
      setValue('relation', data.relation);
      trigger();
    } else {
      reset({
        name: '' as any,
        phoneNumber: '' as any,
        relation: '' as any,
      });
    }
  }, [data, setValue, reset, trigger, open]);

  return (
    <Dialog
      PaperProps={{ style: { maxWidth: 450 } }}
      open={open}
      keepMounted
      onClose={onClose.bind(null, false)}
      classes={{ paper: classes.dialogPaper, scrollPaper: classes.dialogScroll }}>
      <DialogTitle className={classes.dialogTitle}>
        {data?.id ? 'Acil Durumda Aranacak Kişi Güncelle' : 'Acil Durumda Aranacak Kişi Ekle'}
      </DialogTitle>
      <DialogContent>
        <FormProvider {...{ ...methods, reset, getValues, setValue, trigger }}>
          <form noValidate className="mt-2" onSubmit={methods.handleSubmit(handleSubmit)}>
            <Grid container spacing={1}>
              <Grid item md={12} sm={12} lg={12}>
                <TextField required name="name" label="Ad Soyad" />
              </Grid>
              <Grid item md={12} sm={12} lg={12}>
                <TextField required name="relation" label="Yakınlık" />
              </Grid>
              <Grid item md={12} sm={12} lg={12}>
                <TextField required name="phoneNumber" label="Telefon" mask={PHONE_MASK} maskError="Hatalı telefon numarası" />
              </Grid>

              <Grid item md={12} className="text-right">
                <Button variant="contained" type="submit" color="primary" className="mr-2">
                  {data?.id ? 'Güncelle' : 'Kaydet'}
                </Button>
                <Button variant="contained" type="button" onClick={onClose.bind(null, false)}>
                  Vazgeç
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
